import enTrade from "#/integrations/tolgee/messages/en/trade.json";
import koTrade from "#/integrations/tolgee/messages/ko/trade.json";
import zhTrade from "#/integrations/tolgee/messages/zh/trade.json";
import { getTradeMarketName, XYZ_DEX_NAME } from "./hyperliquid.ts";

type TranslationParams = Record<
	string,
	string | number | boolean | null | undefined
>;

export type MarketTranslate = (
	key: string,
	params?: TranslationParams,
) => string;

const PERPETUAL_SUFFIX = "Perpetual Futures";
const MARKET_ASSET_MESSAGES = [enTrade, koTrade, zhTrade] as const;

function translateWithFallback(
	t: MarketTranslate,
	key: string,
	fallback: string,
	params?: TranslationParams,
) {
	const translated = t(key, params);
	return translated && translated !== key ? translated : fallback;
}

export function stripPerpetualSuffix(value: string) {
	return value.replace(/ Perpetual Futures$/, "");
}

export function getDefaultPerpetualDisplayName(coin: string, dex?: string) {
	return `${getTradeMarketName(coin, dex)} ${PERPETUAL_SUFFIX}`;
}

export function getLocalizedMarketName({
	coin,
	dex,
	t,
}: {
	coin: string;
	dex?: string;
	t: MarketTranslate;
}) {
	const key = getMarketAssetTranslationKey(coin, dex);
	return translateWithFallback(t, key, getTradeMarketName(coin, dex));
}

function getMarketAssetTranslationKey(coin: string, dex?: string) {
	return dex === XYZ_DEX_NAME
		? `market.asset.xyz.${coin.toLowerCase()}`
		: `market.asset.${coin.toLowerCase()}`;
}

export function getSearchableMarketNames({
	coin,
	dex,
}: {
	coin: string;
	dex?: string;
}) {
	const key = getMarketAssetTranslationKey(coin, dex);
	const fallback = getTradeMarketName(coin, dex);

	return Array.from(
		new Set([
			fallback,
			...MARKET_ASSET_MESSAGES.map(
				(messages) => (messages as Record<string, string>)[key],
			).filter(
				(value): value is string =>
					typeof value === "string" && value.length > 0,
			),
		]),
	);
}

export function getLocalizedPerpetualDisplayName({
	coin,
	dex,
	t,
}: {
	coin: string;
	dex?: string;
	t: MarketTranslate;
}) {
	const name = getLocalizedMarketName({ coin, dex, t });
	const fallback = getDefaultPerpetualDisplayName(coin, dex);

	return translateWithFallback(t, "market.format.perpetual-futures", fallback, {
		name,
	});
}
